/**
 * 실무에서 사용하는 간단한 API 헬퍼 함수들 (JavaScript 버전)
 * 기존 응답 구조를 유지하면서 필요할 때만 변환
 */

/**
 * @typedef {Object} ApiResponse
 * @property {boolean} success - API 호출 성공 여부
 * @property {string} message - 응답 메시지
 * @property {string} resultCode - 결과 코드
 * @property {any} data - 실제 데이터
 */

/**
 * @typedef {Object} WrapperResponse
 * @property {Object} content - 래핑된 데이터
 * @property {string} message - 응답 메시지
 * @property {string} resultCode - 결과 코드
 */

/**
 * @typedef {Object} SiteDesign
 * @property {string} id - 사이트 디자인 ID
 * @property {string} name - 사이트 디자인 이름
 * @property {string} description - 사이트 디자인 설명
 * @property {string} status - 사이트 디자인 상태
 */

/**
 * API 응답에서 실제 데이터를 추출합니다.
 * @param {ApiResponse|WrapperResponse} response - API 응답 객체
 * @returns {any} 추출된 데이터
 */
export function extractData(response) {
  // Axios 응답인 경우 (response.data가 있는 경우)
  if (response && response.data) {
    const data = response.data;
    // content 래퍼가 있는 경우
    if (data.content !== undefined) {
      return data.content;
    }
    // 직접 데이터인 경우
    return data;
  }

  // 직접 응답인 경우 (response.data가 없는 경우)
  if (response && response.content !== undefined) {
    return response.content;
  }

  // 데이터가 없는 경우
  return response;
}

/**
 * API 응답이 성공인지 확인합니다.
 * @param {ApiResponse|WrapperResponse} response - API 응답 객체
 * @returns {boolean} 성공 여부
 */
export function isSuccess(response) {
  // Axios 응답인 경우
  if (response && response.data) {
    const data = response.data;
    return data.result === 'S' || data.resultCode === 0;
  }

  // 직접 응답인 경우
  return response.result === 'S'  || response.resultCode === 0;
}

/**
 * API 응답에서 에러 메시지를 추출합니다.
 * @param {ApiResponse|WrapperResponse} response - API 응답 객체
 * @returns {string} 에러 메시지
 */
export function getErrorMessage(response) {
  // Axios 응답인 경우
  if (response && response.data) {
    return response.data.resultDesc || '알 수 없는 오류가 발생했습니다.';
  }

  // 직접 응답인 경우
  return response.resultDesc || '알 수 없는 오류가 발생했습니다.';
}

/**
 * API 응답에서 결과 코드를 추출합니다.
 * @param {ApiResponse|WrapperResponse} response - API 응답 객체
 * @returns {string} 결과 코드
 */
export function getResultCode(response) {
  // Axios 응답인 경우
  if (response && response.data) {
    return response.data.resultCode || '';
  }

  // 직접 응답인 경우
  return response.resultCode || '';
}

/**
 * Wrapper 형태 응답인지 확인
 * Axios 응답이면 response.data에서 확인, 직접 응답이면 response에서 확인
 *
 * @param {ApiResponse | WrapperResponse | null | undefined} response - API 응답
 * @returns {boolean} Wrapper 형태 여부
 */
export const isWrapperResponse = (response) => {
  if (!response) return false;

  // Axios 응답인지 확인 (response.data가 있는 경우)
  let actualResponse = response;
  if (response.data !== undefined) {
    actualResponse = response.data;
  }

  return 'content' in actualResponse &&
         'result' in actualResponse &&
         'resultCode' in actualResponse &&
         'resultDesc' in actualResponse;
};

/**
 * 직접 데이터 형태 응답인지 확인
 *
 * @param {ApiResponse | WrapperResponse | null | undefined} response - API 응답
 * @returns {boolean} 직접 데이터 형태 여부
 */
export const isDirectResponse = (response) => {
  if (!response) return false;

  return !isWrapperResponse(response);
};

/**
 * Axios 응답인지 확인
 *
 * @param {ApiResponse | WrapperResponse | null | undefined} response - API 응답
 * @returns {boolean} Axios 응답 여부
 */
export const isAxiosResponse = (response) => {
  return response && response.data !== undefined;
};

/**
 * API 응답의 result 필드가 "F"인지 확인하고, 실패인 경우 에러를 throw합니다.
 * UI 레벨에서 API 호출 후 응답을 검증할 때 사용합니다.
 * 
 * @param {any} response - API 응답 (response.data 또는 직접 응답)
 * @returns {any} 검증 통과 시 원본 응답 반환
 * @throws {Error} result가 "F"인 경우 resultDesc를 메시지로 하는 에러
 * 
 * @example
 * try {
 *   const response = await someService.someMethod();
 *   throwErrorIfFailed(response);
 *   // 성공 처리
 * } catch (error) {
 *   // 에러 처리
 * }
 */
export function throwErrorIfFailed(response) {
  // response.data가 있는 경우 (직접 응답)
  // 또는 response 자체가 데이터인 경우
  const data = response?.data || response;
  
  if (data && typeof data.result === 'string' && data.result === 'F') {
    const error = new Error(data.resultDesc || '서버 오류가 발생했습니다.');
    error.resultCode = data.resultCode;
    error.resultDesc = data.resultDesc;
    throw error;
  }
  
  return response;
}

/**
 * API 응답의 result 필드가 "F"인지 확인하고, 실패인 경우 alert를 표시한 후 에러를 throw합니다.
 * UI 레벨에서 API 호출 후 즉시 사용자에게 알림을 표시하고 싶을 때 사용합니다.
 * 
 * @param {any} response - API 응답 (response.data 또는 직접 응답)
 * @param {string} defaultErrorMessage - resultDesc가 없을 때 사용할 기본 에러 메시지
 * @returns {any} 검증 통과 시 원본 응답 반환
 * @throws {Error} result가 "F"인 경우 resultDesc를 메시지로 하는 에러
 * 
 * @example
 * try {
 *   const response = await someService.someMethod();
 *   showAlertAndThrowErrorIfFailed(response);
 *   // 성공 처리
 * } catch (error) {
 *   // alert는 이미 표시되었으므로 추가 처리만 수행
 * }
 */
export function showAlertAndThrowErrorIfFailed(response, defaultErrorMessage = '서버 오류가 발생했습니다.') {
  // response.data가 있는 경우 (직접 응답)
  // 또는 response 자체가 데이터인 경우
  const data = response?.data || response;
  
  if (data && typeof data.result === 'string' && data.result === 'F') {
    const errorMessage = data.resultDesc || defaultErrorMessage;
    alert(errorMessage);
    const error = new Error(errorMessage);
    error.resultCode = data.resultCode;
    error.resultDesc = data.resultDesc;
    throw error;
  }
  
  return response;
}

/**
 * AbortController/axios 취소로 중단된 요청인지 판별
 * @param {unknown} error
 * @returns {boolean}
 */
export function isRequestAborted(error) {
  if (!error) {
    return false;
  }
  if (error.code === 'ERR_CANCELED' || error.name === 'AbortError' || error.name === 'CanceledError') {
    return true;
  }
  const message = typeof error.message === 'string' ? error.message : '';
  return message.includes('aborted') || message.includes('canceled');
}
